import React, { useContext, useState, useEffect } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import API from "../../api/api";
import {
  FaRunning,
  FaCalendarCheck,
  FaChartLine,
  FaFileAlt,
  FaClipboardCheck,
  FaSignOutAlt,
  FaCampground,
  FaBars,
  FaTimes,
  FaUser,
  FaTshirt,
  FaClipboardList,
} from "react-icons/fa";

const CoachLayout = () => {
  const { user, logout } = useContext(AuthContext);
  const [activeCamp, setActiveCamp] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchActiveCamp = async () => {
      try {
        const res = await API.camp.coachPortal.getActiveCamp();
        if (res?.data?.camp || res?.camp) {
          setActiveCamp(res?.data?.camp || res?.camp);
        }
      } catch (err) {
        console.error("Failed to load active camp", err);
      }
    };
    fetchActiveCamp();
  }, []);

  const navItems = [
    { to: "/coach", label: "Dashboard", icon: FaChartLine, end: true },
    { to: "/coach/athletes", label: "My Athletes", icon: FaRunning },
    { to: "/coach/kits", label: "Kit & Caste", icon: FaTshirt },
    { to: "/coach/attendance", label: "Attendance", icon: FaCalendarCheck },
    { to: "/coach/daily-reports", label: "Daily Reports", icon: FaClipboardList },
    { to: "/coach/report-cards", label: "Report Cards", icon: FaClipboardCheck },
    { to: "/coach/weekly-reports", label: "Weekly Reports", icon: FaFileAlt },
    { to: "/coach/camp-reports", label: "Camp Reports", icon: FaChartLine },
  ];

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 flex flex-col">
      {/* Top Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Portal Brand */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-600 to-cyan-700 flex items-center justify-center shadow-md shadow-cyan-700/20">
                <FaRunning className="text-white text-xl" />
              </div>
              <div>
                <span className="font-bold text-base tracking-wide text-slate-900 flex items-center gap-2">
                  Coach Portal
                  <span className="text-[10px] bg-cyan-50 text-cyan-700 font-bold px-2 py-0.5 rounded-full border border-cyan-200">
                    Official
                  </span>
                </span>
                <p className="text-[11px] text-slate-500 font-medium leading-none">
                  Para Sports Association of Andhra Pradesh
                </p>
              </div>
            </div>

            {/* Active Camp Badge */}
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-xl text-xs">
              <FaCampground className="text-amber-600" />
              <span className="text-slate-600 font-medium">Active Camp:</span>
              <span className="font-bold text-amber-800">
                {activeCamp?.name || "State Coaching Camp 2026"}
              </span>
            </div>

            {/* Right: Coach Profile & Logout */}
            <div className="hidden sm:flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-semibold text-slate-900 leading-tight">
                  {user?.name || "Coach"}
                </p>
                <p className="text-xs text-cyan-700 font-medium">
                  {user?.district ? `${user.district} District` : "Registered Coach"}
                </p>
              </div>

              <div className="w-9 h-9 rounded-full bg-cyan-100 border border-cyan-200 flex items-center justify-center text-cyan-800">
                <FaUser className="text-sm" />
              </div>

              <button
                onClick={logout}
                title="Logout"
                className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <FaSignOutAlt className="text-lg" />
              </button>
            </div>

            {/* Mobile Hamburger */}
            <div className="flex sm:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              >
                {mobileMenuOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
              </button>
            </div>
          </div>
        </div>

        {/* Desktop Navigation Tabs */}
        <div className="hidden sm:block border-t border-slate-200 bg-slate-50/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex space-x-1 py-1.5 overflow-x-auto">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all duration-200 ${
                      isActive
                        ? "bg-cyan-700 text-white shadow-sm"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
                    }`
                  }
                >
                  <item.icon className="text-sm" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
          </div>
        </div>

        {/* Mobile Slide-down Menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-slate-200 bg-white px-4 pt-3 pb-4 space-y-2 shadow-lg">
            <div className="p-3 bg-slate-50 rounded-xl mb-3 flex items-center justify-between border border-slate-200">
              <div>
                <p className="text-sm font-semibold text-slate-900">{user?.name || "Coach"}</p>
                <p className="text-xs text-cyan-700">{user?.mobile}</p>
              </div>
              <button
                onClick={logout}
                className="px-3 py-1.5 text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg flex items-center gap-1.5 font-semibold"
              >
                <FaSignOutAlt /> Logout
              </button>
            </div>

            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? "bg-cyan-700 text-white font-semibold shadow-sm"
                      : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                <item.icon />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        )}
      </header>

      {/* Main Page Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-4 text-center text-xs text-slate-500 print:hidden">
        Para Sports Association of Andhra Pradesh &bull; Coaching Camp Management Portal &bull; All Rights Reserved
      </footer>

    </div>
  );
};

export default CoachLayout;
